import { Component, signal, computed, input, output, inject, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';

import { Subscription } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { ProgressBar } from '@openng/optimus-ui/progressbar';
import { MessageModule } from '@openng/optimus-ui/message';
import { Select } from '@openng/optimus-ui/select';

import { FileUploadItem, UploadMode } from 'src/app/models/file-upload-item';
import { FileUploadContext } from 'src/app/models/file-upload-context';
import { FileType, FILE_TYPE_OPTIONS } from 'src/app/models/file-type.constants';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { ImportProgressService } from 'src/app/services/import-progress.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { getChartColors } from 'src/app/theme/design-tokens';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { environment } from 'src/environments/environment';
/**
 * Reusable file upload component with drag-drop, progress tracking, and retry functionality
 * Implements Figma mockup design with multi-file support
 */
@Component({
	selector: 'app-file-upload',
	imports: [FormsModule, ButtonModule, ProgressBar, MessageModule, Select, ConfirmationDialogComponent],
	templateUrl: './file-upload.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './file-upload.component.scss',
})
export class FileUploadComponent {
	private fileUploadService = inject(FileUploadService);

	private importProgressService = inject(ImportProgressService);

	private environmentService = inject(EnvironmentService);

	// File input reference
	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

	// Inputs
	uploadMode = input.required<UploadMode>();

	context = input<FileUploadContext | null>(null);

	maxFileSize = input<number>(3145728); // 3MB default

	acceptedTypes = input<string>('JUnit XML, pytest XML, ILab, VTest Studio XML, Images, Videos');

	hasEnvironment = input<boolean>(false);

	existingFileNames = input<string[]>([]);

	// Whether an XCOM readout file already exists — used to enforce one-per-suite limit
	hasExistingXcom = input<boolean>(false);

	isMainSuite = input<boolean>(false);

	// Optional import-mode hook. When set, dropped files (after extension filtering) are passed
	// to it instead of being uploaded directly; it returns the file(s) to upload now (e.g. a
	// single zip bundling the inputs), or an empty array when it has staged the files and
	// nothing should upload yet. Used by the treo manual import to pair a results spreadsheet
	// with an ECU readout. Other modes/environments leave this unset and upload as before.
	importPreprocessor = input<((files: File[]) => Promise<File[]>) | null>(null);

	// File type selection (hierarchy mode only)
	selectedFileType = signal<FileType | null>(null);

	readonly fileTypeOptions = computed(() => {
		const ctx = this.context();
		const isMainSuite = !ctx?.analyzerSubTestSuiteUid && !ctx?.analyzerTestCaseUid;
		return isMainSuite ? FILE_TYPE_OPTIONS : FILE_TYPE_OPTIONS.filter(o => !o.mainSuiteOnly);
	});

	// Outputs
	filesUploaded = output<void>();

	uploadError = output<string>();

	// Emits pending XCOM files when the user confirms a replace — parent should delete
	// the old readout file first, then call enqueueAndUpload() to proceed with upload.
	xcomReplaceConfirmed = output<File[]>();

	// State
	fileQueue = signal<FileUploadItem[]>([]);

	isDragOver = signal<boolean>(false);

	isUploading = signal<boolean>(false);

	hasHadFiles = signal<boolean>(false);

	// Overwrite confirmation dialog state
	overwriteDialogVisible = signal<boolean>(false);

	private pendingOverwriteFiles: { all: File[]; clean: File[] } | null = null;

	// XCOM replace confirmation dialog state
	xcomReplaceDialogVisible = signal<boolean>(false);

	private pendingXcomFiles: File[] | null = null;

	// Active upload subscriptions keyed by item id — used to cancel in-flight requests
	private activeUploads = new Map<string, Subscription>();

	// Timer for auto-clearing queue after upload (import mode only)
	private autoClearTimer?: ReturnType<typeof setTimeout>;

	// Semantic colors for status display - uses design tokens
	readonly colors = getChartColors(false);

	// Enable verbose logging in non-production environments
	private isDev = !environment.IsProduction;

	/**
	 * Opens the file selector dialog
	 */
	public openFileSelector() {
		if (this.isDev) {
			console.log('[FileUpload] Opening file selector, fileInput:', this.fileInput);
		}
		if (this.fileInput && this.fileInput.nativeElement) {
			this.fileInput.nativeElement.click();
		} else {
			console.error('[FileUpload] File input not available');
		}
	}

	/**
	 * Handles file selection from file input
	 * @param event
	 */
	public onFileSelect(event: Event) {
		const input = event.target as HTMLInputElement;
		if (input.files) {
			this.addFilesToQueue(Array.from(input.files));
			input.value = ''; // Reset input
		}
	}

	/**
	 * Handles drag over event
	 * @param event
	 */
	public onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver.set(true);
	}

	/**
	 * Handles drag leave event
	 * @param event
	 */
	public onDragLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver.set(false);
	}

	/**
	 * Handles file drop
	 * @param event
	 */
	public onDrop(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver.set(false);

		if (event.dataTransfer?.files) {
			this.addFilesToQueue(Array.from(event.dataTransfer.files));
		}
	}

	/**
	 * Adds files to the upload queue and automatically starts upload.
	 * When the selected file type is XCOM and one already exists, prompts to replace.
	 * @param files
	 */
	private addFilesToQueue(files: File[]) {
		// In hierarchy mode, a file type must be selected before uploading
		if (this.uploadMode() === 'hierarchy' && !this.selectedFileType()) {
			this.uploadError.emit('Please select a file type before uploading.');
			return;
		}

		// In import mode, only allow .xml or .json files
		if (this.uploadMode() === 'import') {
			const accepted = this.acceptedTypes()
				.toLowerCase()
				.split(',')
				.map(t => t.trim());
			const rejected = files.filter(f => {
				const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
				return !accepted.includes(ext);
			});
			if (rejected.length > 0) {
				const names = rejected.map(f => f.name).join(', ');
				this.uploadError.emit(`Only ${this.acceptedTypes()} files are supported for import. Rejected: ${names}`);
				files = files.filter(f => !rejected.includes(f));
				if (files.length === 0) return;
			}

			// When a preprocessor is supplied (treo manual import), let it stage/bundle the files.
			// It returns the file(s) to upload now, or an empty array when it has staged them and
			// nothing should upload yet.
			const preprocess = this.importPreprocessor();
			if (preprocess) {
				preprocess(files)
					.then(toUpload => {
						if (toUpload.length > 0) {
							this.enqueueWithDuplicateCheck(toUpload);
						}
					})
					.catch(err => {
						console.error('[FileUpload] Import preprocessor failed:', err);
						this.uploadError.emit('Failed to prepare the files for import. Please try again.');
					});
				return;
			}
		}

		// Enforce one XCOM readout file per suite
		if (this.selectedFileType() === 'xcom' && this.hasExistingXcom()) {
			this.pendingXcomFiles = files;
			this.xcomReplaceDialogVisible.set(true);
			return;
		}

		this.enqueueWithDuplicateCheck(files);
	}

	/**
	 * Handles duplicate-check and enqueue for files.
	 * @param files
	 */
	private enqueueWithDuplicateCheck(files: File[]) {
		const existing = new Set(this.existingFileNames().map(n => n.toLowerCase()));
		const duplicates = files.filter(f => existing.has(f.name.toLowerCase()));
		const clean = files.filter(f => !existing.has(f.name.toLowerCase()));

		if (duplicates.length === 0) {
			this.enqueueAndUpload(files);
			return;
		}

		this.pendingOverwriteFiles = { all: files, clean };
		this.overwriteDialogVisible.set(true);
	}

	public confirmXcomReplace() {
		if (this.pendingXcomFiles) {
			this.xcomReplaceConfirmed.emit(this.pendingXcomFiles);
		}
		this.xcomReplaceDialogVisible.set(false);
		this.pendingXcomFiles = null;
	}

	public cancelXcomReplace() {
		this.xcomReplaceDialogVisible.set(false);
		this.pendingXcomFiles = null;
	}

	public overwriteDialogMessage(): string {
		if (!this.pendingOverwriteFiles) return '';
		const existing = new Set(this.existingFileNames().map(n => n.toLowerCase()));
		const duplicates = this.pendingOverwriteFiles.all.filter(f => existing.has(f.name.toLowerCase()));
		const names = duplicates.map(f => f.name).join('<br>');
		return duplicates.length === 1
			? `A file named <strong>${names}</strong> is already attached.<br>Do you want to overwrite it?`
			: `Files already attached: <strong><br>${names}</strong>.<br>Do you want to overwrite them?`;
	}

	public confirmOverwrite() {
		if (this.pendingOverwriteFiles) {
			this.enqueueAndUpload(this.pendingOverwriteFiles.all);
		}
		this.overwriteDialogVisible.set(false);
		this.pendingOverwriteFiles = null;
	}

	public cancelOverwrite() {
		if (this.pendingOverwriteFiles?.clean.length) {
			this.enqueueAndUpload(this.pendingOverwriteFiles.clean);
		}
		this.overwriteDialogVisible.set(false);
		this.pendingOverwriteFiles = null;
	}

	public enqueueAndUpload(files: File[]) {
		const newItems: FileUploadItem[] = files.map(file => ({
			file,
			status: 'pending' as const,
			progress: 0,
			id: `${file.name}-${Date.now()}-${Math.random()}`,
		}));

		this.fileQueue.update(queue => [...queue, ...newItems]);
		this.hasHadFiles.set(true);
		if (this.isDev) {
			console.log('[FileUpload] Added files to queue:', newItems.length);
		}

		// Start upload for both import and hierarchy modes
		void this.uploadAll();
	}

	/**
	 * Web analytics: one event per successful batch (no filenames).
	 * — manual_import_upload_success: sidebar Manual Import (import flow).
	 * — suite_upload_main_success: file attached at main suite (files dialog, suite root).
	 * — suite_upload_nested_success: sub-suite or test case attachment (“single file” context).
	 * @param fileCount
	 */
	private trackUploadSuccessBatch(fileCount: number): void {
		if (typeof window === 'undefined' || fileCount < 1) {
			return;
		}
		const mode = this.uploadMode();
		if (mode === 'import') {
			window.AnalyticsSDK?.track('manual_import_upload_success', { fileCount });
			return;
		}
		if (mode === 'hierarchy') {
			const level = this.hierarchyAttachmentLevel();
			const eventName = level === 'mainSuite' ? 'suite_upload_main_success' : 'suite_upload_nested_success';
			window.AnalyticsSDK?.track(eventName, { fileCount, attachmentLevel: level });
			return;
		}
	}

	/** Where in the suite tree hierarchy uploads attach (import mode does not use this). */
	private hierarchyAttachmentLevel(): 'mainSuite' | 'subSuite' | 'testCase' {
		const ctx = this.context();
		if (!ctx) {
			return 'mainSuite';
		}
		if (ctx.analyzerTestCaseUid) {
			return 'testCase';
		}
		if (ctx.analyzerSubTestSuiteUid) {
			return 'subSuite';
		}
		return 'mainSuite';
	}

	/**
	 * Removes a file from the queue
	 * @param item
	 */
	public removeFile(item: FileUploadItem) {
		// Cancel the in-flight request if this file is currently uploading
		const sub = this.activeUploads.get(item.id);
		if (sub) {
			sub.unsubscribe();
			this.activeUploads.delete(item.id);
			if (this.isDev) {
				console.log('[FileUpload] Cancelled upload for:', item.file.name);
			}
		}
		this.fileQueue.update(queue => queue.filter(f => f.id !== item.id));
		if (this.isDev) {
			console.log('[FileUpload] Removed file:', item.file.name);
		}
	}

	/**
	 * Uploads all pending/error files in the queue
	 */
	public async uploadAll() {
		const itemsToUpload = this.fileQueue().filter(item => item.status === 'pending' || item.status === 'error');

		if (itemsToUpload.length === 0) {
			console.warn('[FileUpload] No files to upload');
			return;
		}

		// Prevent uploading if already in progress
		if (this.isUploading()) {
			console.warn('[FileUpload] Upload already in progress, skipping');
			return;
		}

		this.isUploading.set(true);

		// Upload files sequentially
		for (const item of itemsToUpload) {
			await this.uploadFile(item);
		}

		this.isUploading.set(false);

		// Check if all uploads succeeded
		const allSuccess = this.fileQueue().every(item => item.status === 'success');
		const anyError = this.fileQueue().some(item => item.status === 'error');

		if (allSuccess) {
			this.trackUploadSuccessBatch(itemsToUpload.length);

			// Show progress banner immediately in import mode (Preparing... until first event)
			if (this.uploadMode() === 'import') {
				const environment = this.environmentService.getCurrentEnvironment();
				const firstFileName = itemsToUpload[0]?.file?.name;
				if (environment) {
					this.importProgressService.notifyPreparing(environment, firstFileName);
				}
			}

			this.filesUploaded.emit();

			// Auto-clear queue after 20 seconds in import mode (debounced)
			if (this.uploadMode() === 'import') {
				// Clear any existing timer
				if (this.autoClearTimer) {
					clearTimeout(this.autoClearTimer);
				}

				// Set new timer to clear after 20 seconds
				this.autoClearTimer = setTimeout(() => {
					this.reset();
					if (this.isDev) {
						console.log('[FileUpload] Queue auto-cleared after 20 seconds of inactivity');
					}
				}, 20000);
			}
		} else if (anyError) {
			console.warn('[FileUpload] Some uploads failed');
			this.uploadError.emit('Some files failed to upload. Click retry to try again.');
		}
	}

	/**
	 * Retries upload for a specific failed file
	 * @param item
	 */
	public async retryFile(item: FileUploadItem) {
		await this.uploadFile(item);
	}

	/**
	 * Uploads a single file
	 * @param item
	 */
	private uploadFile(item: FileUploadItem): Promise<void> {
		return new Promise(resolve => {
			// Check if file is already successfully uploaded
			const currentItem = this.fileQueue().find(f => f.id === item.id);
			if (currentItem?.status === 'success') {
				if (this.isDev) {
					console.log('[FileUpload] File already uploaded successfully:', item.file.name);
				}
				resolve();
				return;
			}

			// Update item status to uploading
			this.updateFileItem(item.id, { status: 'uploading', progress: 0, errorMessage: undefined });

			const mode = this.uploadMode();
			const ctx = this.context();

			if (mode === 'hierarchy' && !ctx) {
				console.error('[FileUpload] Hierarchy mode requires context');
				this.updateFileItem(item.id, {
					status: 'error',
					progress: 0,
					errorMessage: 'Missing upload context',
				});
				resolve();
				return;
			}

			// Create upload context for hierarchy mode
			const uploadContext: FileUploadContext | null = mode === 'hierarchy' && ctx ? { ...ctx, fileName: item.file.name, fileType: this.selectedFileType() ?? undefined } : null;

			// Choose upload method based on mode
			const upload$ = mode === 'hierarchy' && uploadContext ? this.fileUploadService.uploadFile$(item.file, uploadContext) : this.fileUploadService.uploadImportFile$(item.file);

			let uploadCompleted = false;

			const sub = upload$.subscribe({
				next: event => {
					// Don't process events if upload already completed
					if (uploadCompleted) return;

					if (event.type === HttpEventType.UploadProgress && event.total) {
						const progress = Math.round((100 * event.loaded) / event.total);
						this.updateFileItem(item.id, { progress });
						if (this.isDev) {
							console.log(`[FileUpload] ${item.file.name}: ${progress}%`);
						}
					} else if (event.type === HttpEventType.Response) {
						uploadCompleted = true;
						this.activeUploads.delete(item.id);
						if (this.isDev) {
							console.log('[FileUpload] Upload successful:', item.file.name);
						}
						this.updateFileItem(item.id, { status: 'success', progress: 100 });
						resolve();
					}
				},
				error: (error: any) => {
					this.activeUploads.delete(item.id);
					// Don't process error if upload already completed successfully
					if (uploadCompleted) {
						if (this.isDev) {
							console.log('[FileUpload] Ignoring error after successful upload:', item.file.name);
						}
						return;
					}

					console.error('[FileUpload] Upload failed:', item.file.name, error);
					this.updateFileItem(item.id, {
						status: 'error',
						progress: 0,
						errorMessage: error.message || 'Upload failed',
					});
					resolve();
				},
			});

			// Only track the subscription if the item wasn't already removed
			if (this.fileQueue().some(f => f.id === item.id)) {
				this.activeUploads.set(item.id, sub);
			} else {
				// Item was removed before the subscribe callback ran — cancel immediately
				sub.unsubscribe();
				resolve();
			}
		});
	}

	/**
	 * Updates a file item in the queue
	 * Prevents overwriting successful uploads
	 * @param id
	 * @param updates
	 */
	private updateFileItem(id: string, updates: Partial<FileUploadItem>) {
		this.fileQueue.update(queue =>
			queue.map(item => {
				if (item.id !== id) return item;

				// Prevent changing status from success to anything else
				if (item.status === 'success' && updates.status && updates.status !== 'success') {
					console.warn('[FileUpload] Prevented status change from success to', updates.status, 'for', item.file.name);
					return item;
				}

				return { ...item, ...updates };
			}),
		);
	}

	/**
	 * Formats file size for display
	 * @param bytes
	 */
	public formatFileSize(bytes: number): string {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	}

	/**
	 * Gets status icon for file item
	 * @param item
	 */
	public getStatusIcon(item: FileUploadItem): string {
		switch (item.status) {
			case 'success':
				return 'pi pi-check-circle';
			case 'error':
				return 'pi pi-times-circle';
			case 'uploading':
				return 'pi pi-spin pi-spinner';
			default:
				return 'pi pi-file';
		}
	}

	/**
	 * Gets status color for file item
	 * @param item
	 */
	public getStatusColor(item: FileUploadItem): string {
		switch (item.status) {
			case 'success':
				return this.colors.success.primary;
			case 'error':
				return this.colors.danger.primary;
			case 'uploading':
				return this.colors.info.primary;
			default:
				return 'var(--p-text-color)';
		}
	}

	/**
	 * Gets status message for file item
	 * @param item
	 */
	public getStatusMessage(item: FileUploadItem): string {
		switch (item.status) {
			case 'success':
				return 'Upload successful';
			case 'error':
				return item.errorMessage || 'Upload failed';
			case 'uploading':
				return `Uploading... ${item.progress}%`;
			default:
				return 'Ready to upload';
		}
	}

	/**
	 * Checks if upload button should be enabled
	 */
	public canUpload(): boolean {
		const queue = this.fileQueue();
		return queue.length > 0 && !this.isUploading() && queue.some(item => item.status === 'pending' || item.status === 'error');
	}

	/**
	 * Clears all successfully uploaded files
	 */
	public clearSuccessful() {
		this.fileQueue.update(queue => queue.filter(item => item.status !== 'success'));
	}

	/**
	 * Checks if there are any successful uploads
	 */
	public hasSuccessfulUploads(): boolean {
		return this.fileQueue().some(item => item.status === 'success');
	}

	/**
	 * Resets the component state (clears all files from queue)
	 */
	public reset() {
		// Clear auto-clear timer if it exists
		if (this.autoClearTimer) {
			clearTimeout(this.autoClearTimer);
			this.autoClearTimer = undefined;
		}

		this.activeUploads.forEach(sub => sub.unsubscribe());
		this.activeUploads.clear();
		this.fileQueue.set([]);
		this.isDragOver.set(false);
		this.isUploading.set(false);
		this.hasHadFiles.set(false);
		this.selectedFileType.set(null);
	}
}
